import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { Certificate } from 'app/library/certificate/models/certificate.model';
import { CertificateService } from 'app/library/certificate/services/certificate.service';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { EditManagerPermissionsDialogComponent } from 'app/library/module-managers/components/dialogs/edit-manager-permissions-dialog/edit-manager-permissions-dialog.component';
import { ModuleManagersService } from 'app/library/module-managers/services/module-managers.service';
import {
    AddUsersToModulesAsManagers,
    AddUsersToModulesAsManagersSuccess,
    AddUsersToModulesAsParticipants,
    AddUsersToModulesAsParticipantsSuccess,
    RemoveUserAsResponsibleSuccess,
    RemoveUsersFromModulesAsManagers,
    UpdateModuleUserResponsibleFailure,
} from 'app/library/module-user/actions';
import { ModuleUserExtended } from 'app/library/module-user/models/module-user.extended.model';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { ModuleStatuses } from 'app/library/module/models/module-statuses';
import { ModuleGlobalService } from 'app/library/module/services/module-global.service';
import { SortMyEndedModules } from 'app/library/my-ended-modules-paginated/actions';
import { MaxBrainTag } from 'app/library/tag/models/tag.model';
import { TeamCourseMemberDetailsDialogComponent } from 'app/main/pages/modules/module-form/module-form-participants-tab/team-course-member-details-dialog/team-course-member-details-dialog.component';
import { TeamProgramMemberDetailsDialogComponent } from 'app/main/pages/modules/module-form/module-form-participants-tab/team-program-member-details-dialog/team-program-member-details-dialog.component';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { NameDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/name/name.component';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    RemoveUserAsResponsible,
    RemoveUsersFromModulesAsManagersSuccess,
    UpdateModuleUserResponsible,
    UpdateModuleUserResponsibleSuccess,
} from '../../../module-user/actions/index';
import { ModuleCategory } from '../../../module/enums/moduleCategory';
import { Module } from '../../../module/models/module.model';
import { TagMultiSelectService } from '../../../tag/services/multi-select.service';
import { UserModuleRemoveParticipantService } from '../../services/user-module-remove-participant.service';
import { EndedModuleDataSource } from './ended-module-datasource';

@Component({
    selector: 'app-my-ended-modules-list-table',
    templateUrl: './my-ended-modules-list-table.component.html',
    styleUrls: ['./my-ended-modules-list-table.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyEndedModulesListTableComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    myEndedModuleUser$: Observable<ModuleUserExtended[]>;

    @Input()
    searchTerm: string;

    @Input()
    displayedColumns: string[];

    @Input()
    totalCount$: Observable<number>;

    @Input()
    currentUserId: string;

    @Input()
    isTeamMembersPage?: boolean;

    @Input()
    enableModuleRedirect?: boolean = true;

    @Input()
    canDownloadCertificates: boolean = true;

    @Output()
    clickedTag = new EventEmitter<MaxBrainTag>();

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    profileId = this._store.selectSnapshot(UserState).me;
    moduleStatuses = ModuleStatuses;
    dataSource: EndedModuleDataSource;
    ModuleKind = ModuleKind;

    get hasTenantAdminOrUsersViewPermission(): boolean {
        return this.profile.permissions.includes(AdminPermissions.USERS_READ) || this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT);
    }

    profile = this._store.selectSnapshot(UserState.getMyInfo);

    constructor(
        public tagMultiSelectService: TagMultiSelectService,
        private _action$: Actions,
        private _store: Store,
        private moduleGlobalService: ModuleGlobalService,
        private _moduleManagersService: ModuleManagersService,
        private _certificateService: CertificateService,
        private _matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private _translateService: TranslateService,
        private _userModuleRemoveParticipantService: UserModuleRemoveParticipantService
    ) {
        super();
    }

    ngOnInit(): void {
        this.dataSource = new EndedModuleDataSource(this.myEndedModuleUser$, this._store, this._action$, this.tagMultiSelectService);
        setTimeout(() => {
            this.paginator.page.subscribe(({ length, pageIndex, pageSize }: PageEvent) => {
                const itemsleft = length - pageIndex * pageSize;
                const itemsInThisPage = itemsleft < pageSize ? itemsleft : pageSize;
                // FIXME: these hardcoded numbers represent the height of the rest of the elements surrounding the table within the layout content
                const tableHeight = 80 + 24 + 64 + 56 + itemsInThisPage * 65 + 72 + 56;
                const contentElementHeight = document.getElementById('container-2').offsetHeight;
                const scrollTop = tableHeight > contentElementHeight ? tableHeight - contentElementHeight : 0;

                setTimeout(() => {
                    document.getElementById('container-3').scrollTop = scrollTop;
                });
            });

            this.loadEndedModulesPage(false);

            this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                this._store.dispatch(new SortMyEndedModules(sort));

                this.loadEndedModulesPage(true);
            });

            this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                this.loadEndedModulesPage(false);
            });
        });
    }

    filterByTag(tag: MaxBrainTag): void {
        this.clickedTag.emit(tag);
    }

    loadEndedModulesPage(resetPageIndex = false): void {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        this.dataSource.loadModules(
            ModuleCategory.Ended,
            this.searchTerm,
            this.sort.active,
            this.sort.direction,
            this.paginator.pageSize,
            this.paginator.pageIndex + 1,
            this.currentUserId,
            this.tagMultiSelectService.selectedTags.map((tag) => parseInt(tag.id))
        );
    }

    goToModule(module: Module): void {
        // On Team Members page we want to show user state on the module
        if (this.isTeamMembersPage) {
            if (module.kind === ModuleKind.Normal) {
                this.openTeamCourseMemberDetailsDialog(module);
            } else {
                this.openTeamProgramMemberDetailsDialog(module);
            }
        } else {
            this.moduleGlobalService.setParentUrl(window.location.pathname);
            this._moduleManagersService.goToModule(this.profileId, module, 'overview');
        }
    }

    openTeamProgramMemberDetailsDialog(currentModule: Module): void {
        const dialog = this._matDialog.open(TeamProgramMemberDetailsDialogComponent, {
            disableClose: false,
            width: '1125px',
            data: {
                moduleId: currentModule.id,
                title: currentModule.name,
                participantId: this.currentUserId,
            },
        });

        dialog.componentInstance.currentModule = currentModule;
    }

    openTeamCourseMemberDetailsDialog(currentModule: Module): void {
        const dialogRef = this._matDialog.open(TeamCourseMemberDetailsDialogComponent, {
            disableClose: false,
            width: '1125px',
        });

        dialogRef.componentInstance.moduleId = currentModule.id;
        dialogRef.componentInstance.title = currentModule.name;
        dialogRef.componentInstance.participantId = this.currentUserId;
    }

    addAsParticipant(moduleUser: ModuleUser): void {
        this._store.dispatch(
            new AddUsersToModulesAsParticipants({
                userIds: [moduleUser.userId],
                moduleIds: [moduleUser.moduleId],
            })
        );

        this._action$.pipe(ofActionDispatched(AddUsersToModulesAsParticipantsSuccess), takeUntil(this._unsubscribeAll)).subscribe((action) => {
            const successMessage = this._translateService.instant('GENERAL.MESSAGE.SAVED');

            // Show the success message
            this._matSnackBar.open(successMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.medium,
            });
        });
    }

    removeAsParticipant(moduleUser: ModuleUserExtended): void {
        this._userModuleRemoveParticipantService.removeAsParticipant(moduleUser, this._unsubscribeAll, () => this.loadEndedModulesPage());
    }

    addAsManager(moduleUser: ModuleUser): void {
        const dialogRef = this._matDialog.open(EditManagerPermissionsDialogComponent, {
            disableClose: false,
            width: '632px',
            autoFocus: false,
        });

        dialogRef.componentInstance.title = this._translateService.instant('LIST.USER_MODULES.CHOOSE_PERMISSIONS');

        dialogRef.afterClosed().subscribe(
            (result: {
                data: {
                    permissions: string[];
                };
            }) => {
                if (result && result.data) {
                    this._store.dispatch(
                        new AddUsersToModulesAsManagers({
                            userIds: [moduleUser.userId],
                            moduleIds: [moduleUser.moduleId],
                            permissions: result.data.permissions,
                        })
                    );

                    this._action$.pipe(ofActionDispatched(AddUsersToModulesAsManagersSuccess), takeUntil(this._unsubscribeAll)).subscribe((action) => {
                        const successMessage = this._translateService.instant('GENERAL.MESSAGE.SAVED');

                        this._matSnackBar.open(successMessage, 'OK', {
                            verticalPosition: 'top',
                            duration: SnackBarTime.medium,
                        });
                    });
                }
            }
        );
    }

    removeAsManager(moduleUser: ModuleUser): void {
        const dialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        dialogRef.componentInstance.confirmTitle = 'FORM.ASSIGN_TO_MODULES.FIELD.REMOVE_AS_MANAGER';
        dialogRef.componentInstance.confirmButtonColor = 'warn';
        dialogRef.componentInstance.confirmMessage = 'GENERAL.MESSAGE.REMOVE_CONFIRMATION';
        dialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        dialogRef.afterClosed().subscribe((result: boolean) => {
            if (result) {
                this._store.dispatch(
                    new RemoveUsersFromModulesAsManagers({
                        userIds: [moduleUser.userId],
                        moduleIds: [moduleUser.moduleId],
                    })
                );
            }
        });

        this._action$.pipe(ofActionDispatched(RemoveUsersFromModulesAsManagersSuccess), takeUntil(this._unsubscribeAll)).subscribe((action) => {
            dialogRef.close();
            const successMessage = this._translateService.instant('GENERAL.MESSAGE.SAVED');

            // Show the success message
            this._matSnackBar.open(successMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.medium,
            });
        });
    }

    setAsResponsible(moduleUser: ModuleUser): void {
        const dialogRef = this._matDialog.open(NameDialogComponent, {
            disableClose: false,
            width: '482px',
        });

        dialogRef.componentInstance.dialogTitle = 'FORM.ASSIGN_TO_MODULES.FIELD.ADD_AS_RESPONSIBLE';
        dialogRef.componentInstance.placeholder = 'GENERAL.PROPERTY.ROLE';
        dialogRef.componentInstance.name = moduleUser.roleLabel || '';

        dialogRef.componentInstance.save.subscribe((name: string) => {
            this._store.dispatch(
                new UpdateModuleUserResponsible({
                    userIds: [moduleUser.userId],
                    moduleIds: [moduleUser.moduleId],
                    roleLabel: name,
                })
            );

            const successSubscription = this._action$.pipe(ofActionDispatched(UpdateModuleUserResponsibleSuccess), takeUntil(this._unsubscribeAll)).subscribe((action) => {
                dialogRef.close();
                const successMessage = this._translateService.instant('GENERAL.MESSAGE.SAVED');

                // Show the success message
                this._matSnackBar.open(successMessage, 'OK', {
                    verticalPosition: 'top',
                    duration: SnackBarTime.medium,
                });

                failureSubscription.unsubscribe();
            });

            const failureSubscription = this._action$
                .pipe(ofActionDispatched(UpdateModuleUserResponsibleFailure), takeUntil(this._unsubscribeAll))
                .subscribe((action: UpdateModuleUserResponsibleFailure) => {
                    dialogRef.componentInstance.errors.next(action.payload.response.error);
                    successSubscription.unsubscribe();
                });
        });
    }

    removeAsResponsible(moduleUser: ModuleUser): void {
        const dialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        dialogRef.componentInstance.confirmTitle = 'FORM.ASSIGN_TO_MODULES.FIELD.REMOVE_AS_RESPONSIBLE';
        dialogRef.componentInstance.confirmButtonColor = 'warn';
        dialogRef.componentInstance.confirmMessage = 'GENERAL.MESSAGE.REMOVE_CONFIRMATION';
        dialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        dialogRef.afterClosed().subscribe((result: boolean) => {
            if (result) {
                this._store.dispatch(
                    new RemoveUserAsResponsible({
                        userIds: [moduleUser.userId],
                        moduleIds: [moduleUser.moduleId],
                    })
                );

                this._action$.pipe(ofActionDispatched(RemoveUserAsResponsibleSuccess), takeUntil(this._unsubscribeAll)).subscribe(() => {
                    this.loadEndedModulesPage();
                });
            }
        });
    }

    downloadCertificate(certificate: Certificate): void {
        this._certificateService.downloadFinalGradeCertificate(certificate).then(() => {});
    }

    visualLength(text: string): number {
        const ruler = document.getElementById('ruler-2');
        ruler.innerHTML = text;
        const toReturn = ruler.offsetWidth;
        ruler.innerHTML = '';
        return toReturn;
    }
}
